import { Map } from 'immutable';
import BaseObject from '@repo/utils/models/BaseObject';
import { ID, ReduxAction } from '@repo/utils/types';
import AppState from '../app-state';

class Import extends BaseObject {
  uuid: string;
  groupID: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  localImportID?: string;

  constructor(props) {
    super(props);
    this.uuid = props.uuid;
    this.groupID = String(props.groupID);
    this.status = props.status;
    this.localImportID = props.localImportID;
  }
}

export type ImportsState = Map<ID, Import>;

export const selectImportStatus = (importUUID: string) => (state: AppState) =>
  state.data.imports.get(importUUID)?.status;

export const selectImportStatusByLocalID = (localImportID: string) => (state: AppState) =>
  !!localImportID
    ? state.data.imports.find((importState) => importState.localImportID === localImportID)
    : undefined;

export function ImportsReducer(
  state: ImportsState = Map<ID, Import>(),
  { type, payload }: ReduxAction,
) {
  switch (type) {
    case 'CREATE_CSV_IMPORT_SUCCEEDED':
      return state.set(
        payload.uuid,
        new Import({
          uuid: payload.uuid,
          groupID: String(payload.import_group),
          status: payload.status,
          localImportID: payload.localImportID,
        }),
      );

    case 'CREATE_CSV_IMPORT_FAILED':
      return state.set(
        payload.localImportID,
        new Import({
          localImportID: payload.localImportID,
          error: true,
        }),
      );

    case 'FETCH_ALL_GROUPS_SUCCEEDED':
      const importingGroups = payload.response?.filter(
        (group) => group.bulk_import?.status && group.bulk_import.status !== 'completed',
      );

      if (!importingGroups || importingGroups.length === 0) {
        return state;
      }

      return importingGroups.reduce((importsState, group) => {
        const uuid = group.bulk_import.uuid;
        return importsState.update(
          uuid,
          new Import({
            uuid,
            groupID: group.id,
            status: group.bulk_import.status,
          }),
          (importState) => importState.copyWith({ status: group.bulk_import.status }),
        );
      }, state);

    case 'FETCH_IMPORT_STATUS_SUCCEEDED':
      return state.update(
        payload.uuid,
        new Import({
          uuid: payload.uuid,
          groupID: String(payload.import_group),
          status: payload.status,
        }),
        (importState) => importState.copyWith({ status: payload.status }),
      );

    default:
      return state;
  }
}
